import moment from 'moment'
import { Expose, Transform, Type } from 'class-transformer'
import { AxiosError } from 'axios'
import { List } from 'react-feather'

import { skuit } from 'src/services/skuit'
import { ActionDirection, WorkflowSequence } from 'src/modules/supply_chain/components/Workflow'
import { handleErrorMessage } from 'src/components/Notification'

import { Address } from './Address'
import { Appointment } from './Appointment'
import { Base } from './Base'
import { Carrier } from './Carrier'
import { Client } from './Client'
import { CustomFieldValue } from './CustomFieldValue'
import { Facility } from './Facility'
import { Flag } from './Flag'
import { Retailer } from './Retailer'

enum FreightOrderStatus {
  received = 'Received',
  processing = 'Processing',
  processed = 'Processed',
  shipped = 'Shipped',
  delivered = 'Delivered',
  canceled = 'Canceled',
}

export enum FreightOrderStatusEnum {
  received = 'received',
  processing = 'processing',
  processed = 'processed',
  shipped = 'shipped',
  delivered = 'delivered',
  canceled = 'canceled',
}

export class FreightOrder extends Base {
  static type = 'freightorder'
  static typeDisplay = 'Freight Order'
  static icon = List

  static Status = FreightOrderStatus

  type = FreightOrder.type
  typeDisplay = FreightOrder.typeDisplay
  icon = FreightOrder.icon

  @Expose({ name: 'xid' })
  guid: string = ''

  number?: string

  details?: string

  @Type(() => Facility)
  facility?: Facility

  po?: string
  bol?: string

  name: string = ''

  @Type(() => Client)
  client: Client = new Client()

  consignee?: string

  @Expose({ name: 'custom_fields' })
  @Type(() => CustomFieldValue)
  customFields?: CustomFieldValue[]

  status?: string

  @Type(() => Address)
  address: Address = new Address()

  @Type(() => Carrier)
  carrier?: Carrier = new Carrier()

  @Expose({ name: 'next_appointment' })
  @Type(() => Appointment)
  nextAppointment?: Appointment

  @Expose({ name: 'placed_on' })
  @Transform((value) => (value ? moment(value) : value))
  placedOn?: moment.Moment

  @Expose({ name: 'process_on' })
  @Transform((value) => (value ? moment(value) : value))
  processOn?: moment.Moment

  @Expose({ name: 'processing_on' })
  @Transform((value) => (value ? moment(value) : value))
  processingOn?: moment.Moment

  @Expose({ name: 'processed_on' })
  @Transform((value) => (value ? moment(value) : value))
  processedOn?: moment.Moment

  @Expose({ name: 'shipped_on' })
  @Transform((value) => (value ? moment(value) : value))
  shippedOn?: moment.Moment

  @Expose({ name: 'delivered_on' })
  @Transform((value) => (value ? moment(value) : value))
  deliveredOn?: moment.Moment

  @Expose({ name: 'canceled_on' })
  @Transform((value) => (value ? moment(value) : value))
  canceledOn?: moment.Moment

  @Expose({ name: 'fulfill_on' })
  @Transform((value) => (value ? moment(value) : value))
  fulfillOn?: moment.Moment

  @Expose({ name: 'cannot_ship_before' })
  @Transform((value) => (value ? moment(value) : value))
  cannotShipBefore?: moment.Moment

  @Expose({ name: 'must_ship_by' })
  @Transform((value) => (value ? moment(value) : value))
  mustShipBy?: moment.Moment

  @Expose({ name: 'expected_ship_date' })
  @Transform((value) => (value ? moment(value) : value))
  expectedShipDate?: moment.Moment

  @Expose({ name: 'must_cancel_by' })
  @Transform((value) => (value ? moment(value) : value))
  mustCancelBy?: moment.Moment

  @Type(() => Facility)
  facilities?: Facility[]

  @Type(() => Flag)
  flags?: Flag[]

  @Type(() => Retailer)
  retailer?: Retailer

  @Expose({ name: 'retailer_routing_number'})
  retailerRoutingNumber?: string

  get workflowStatus() {
    if (this.status) {
      return this.status.charAt(0).toUpperCase() + this.status.slice(1)
    }
    return ''
  }

  get workflow(): WorkflowSequence {
    if (this.status === FreightOrderStatusEnum.canceled) {
      return [
        [FreightOrderStatus.received, this.placedOn],
        [FreightOrderStatus.processing, this.processingOn],
        [FreightOrderStatus.processed, this.processedOn],
        [FreightOrderStatus.canceled, this.canceledOn],
      ]
    }
    return [
      [FreightOrderStatus.received, this.placedOn],
      [FreightOrderStatus.processing, this.processingOn],
      [FreightOrderStatus.processed, this.processedOn],
      [FreightOrderStatus.shipped, this.shippedOn],
      [FreightOrderStatus.delivered, this.deliveredOn],
    ]
  }

  getWorkflowActions = (onAction?: () => void): { [status: string]: ActionDirection } => {
    return {
      [FreightOrderStatus.received]: {
        forward: [
          {
            label: 'Start Processing',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'processing' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightOrderStatus.processing]: {
        forward: [
          {
            label: 'Complete Processing',
            onClick: ({ complete, setConfirmation }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'processed' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => {
                  if (
                    // eslint-disable-next-line camelcase
                    error?.response?.data.non_field_errors?.includes(
                      'Order is not completely fulfilled'
                    )
                  ) {
                    setConfirmation?.({
                      error: error.response.data.non_field_errors,
                      prompt:
                        'Do you want to override? Careful, this could lead to short shipping this order?.',
                      submit: 'Mark Processed',
                      action: ({ complete }) => {
                        skuit.sc.freight.orders
                          .patch(this.guid, { status: 'processed', short_ship: true })
                          .then(() => onAction?.())
                          .catch((error: AxiosError) => {
                            handleErrorMessage(error, 'updat', 'Freight Order')
                          })
                          .finally(() => complete())
                      },
                    })
                  } else {
                    handleErrorMessage(error, 'updat', 'Freight Order')
                  }
                })
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightOrderStatus.processed]: {
        forward: [
          {
            label: 'Ship',
            onClick: ({ complete, setConfirmation }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => {
                  if (
                    // eslint-disable-next-line camelcase
                    error?.response?.data.non_field_errors?.includes(
                      'Order is not completely fulfilled'
                    )
                  ) {
                    setConfirmation?.({
                      error: error.response.data.non_field_errors,
                      prompt: 'Do you want to override and short ship this order?.',
                      submit: 'Short Ship',
                      action: ({ complete }) => {
                        skuit.sc.freight.orders
                          .patch(this.guid, { status: 'shipped', short_ship: true })
                          .then(() => onAction?.())
                          .catch((error: AxiosError) => {
                            handleErrorMessage(error, 'updat', 'Freight Order')
                          })
                          .finally(() => complete())
                      },
                    })
                  } else {
                    handleErrorMessage(error, 'updat', 'Freight Order')
                  }
                })
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Resume Processing',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'processing' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightOrderStatus.shipped]: {
        forward: [
          {
            label: 'Deliver',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'delivered' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Unship',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'processed' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightOrderStatus.delivered]: {
        backward: [
          {
            label: 'Undeliver',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightOrderStatus.canceled]: {
        backward: [
          {
            label: 'Uncancel',
            onClick: ({ complete }) => {
              skuit.sc.freight.orders
                .patch(this.guid, { status: 'received' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Freight Order'))
                .finally(() => complete())
            },
          },
        ],
      },
    }
  }
}

export const freightOrderStatusToString = (status: FreightOrderStatusEnum): string => {
  return status[0].toUpperCase() + status.slice(1)
}

export const nextFreightOrderStatus = (actualStatus: string) => {
  switch (actualStatus) {
    case 'received':
      return FreightOrderStatusEnum.processing
    case 'processing':
      return FreightOrderStatusEnum.processed
    case 'processed':
      return FreightOrderStatusEnum.shipped
    case 'shipped':
      return FreightOrderStatusEnum.delivered
    default:
      return actualStatus
  }
}
